'use client';

import {
  createSubscriberQueryFn,
  getSubscriberQueryMetaOptions,
  type QueryKey,
  type UseQueryResult,
  useQueries,
  useQuery,
  useQueryClient,
} from '@morfi/backend-sdk-v2/client';

/**
 * The flat cache envelope `createListHook` writes (see SDK
 * `src/types/PagedData.ts`). Replicated locally because the type is
 * not exported through the public SDK barrel.
 */
export interface LiveListEnvelope<Row> {
  readonly data: ReadonlyArray<Row>;
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
  readonly _indexFields?: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
}

/**
 * Opts the caller back into `data`-change notifications on a Morfi SDK
 * list-hook cache entry.
 *
 * Why this exists: `createListHook` (SDK v2) intentionally sets
 * `notifyOnChangeProps` to a small allowlist (`['isPending', 'status',
 * …]`) on the page-level observer for re-render isolation — list-row
 * leaves are expected to subscribe individually via `<Items>` +
 * `use<Entity>Field`. That works for tile-style feeds, but it breaks
 * surfaces that derive STRUCTURAL state (sectioning, sorting, totals)
 * from the rows array — `query.rows` returns the latest cache snapshot,
 * but without a `data`-aware observer the parent never re-renders, so
 * the snapshot used to compute derived state goes stale on per-row
 * field patches (e.g. a gift status flipping pending → claimed via NATS).
 *
 * This hook installs a no-op `useQuery` against the same `queryKey`
 * with `notifyOnChangeProps: ['data']` so the caller re-renders on cache
 * data changes. It does NOT trigger a fetch (`enabled: false`). The
 * returned value is the same `data ?? fallback` array the caller would
 * read from `query.rows`, but freshly resolved per render.
 *
 * Prefer per-row `<Items>` + field hooks for visual leaves. Use this
 * helper for surfaces that ALSO need fresh rows in the parent to
 * compute derived state (sectioning, sorting, totals, conditional UI
 * gated on aggregate fields).
 */
export function useLiveListData<Row>(
  queryKey: QueryKey,
  fallback: ReadonlyArray<Row> = [],
  entityQueryKey?: (id: string) => QueryKey,
  projectEntityRow?: (row: Row) => Row,
  subscribe: boolean = true,
): ReadonlyArray<Row> {
  const queryClient = useQueryClient();
  const query = useQuery<LiveListEnvelope<Row | string>>({
    queryKey,
    queryFn: createSubscriberQueryFn<LiveListEnvelope<Row | string>>(queryClient, queryKey),
    ...getSubscriberQueryMetaOptions(queryClient, queryKey),
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY,
    notifyOnChangeProps: subscribe ? ['data'] : [],
  });

  const cached = query.data;
  const cachedData = cached?.data;
  const isNormalized =
    entityQueryKey !== undefined &&
    cachedData !== undefined &&
    (cachedData.length === 0 || typeof cachedData[0] === 'string');
  const ids = isNormalized ? (cachedData as ReadonlyArray<string>) : [];
  const entityData = useQueries({
    queries:
      entityQueryKey === undefined
        ? []
        : ids.map((id) => {
            const entityKey = entityQueryKey(id);
            return {
              queryKey: entityKey,
              queryFn: createSubscriberQueryFn<Row>(queryClient, entityKey),
              ...getSubscriberQueryMetaOptions(queryClient, entityKey),
              enabled: false,
              staleTime: Number.POSITIVE_INFINITY,
              notifyOnChangeProps: subscribe ? ['data'] : [],
            };
          }),
    combine: collectEntityData<Row>,
  });

  if (cachedData === undefined) return fallback;
  if (!isNormalized || entityQueryKey === undefined) return cachedData as ReadonlyArray<Row>;
  if (
    hasAllEntityRows(entityData) &&
    projectEntityRow === undefined &&
    cached?._indexFields === undefined
  ) {
    return entityData;
  }

  // Keep the observer result when complete: TanStack structurally shares this
  // array. If an observer is briefly behind its cache entry, rebuild in the
  // list's canonical order from the cache instead of dropping that entity.
  return ids.flatMap((id, index) => {
    const observed = entityData[index];
    const row = observed ?? queryClient.getQueryData<Row>(entityQueryKey(id));
    if (row === undefined) return [];
    const projected = projectEntityRow === undefined ? row : projectEntityRow(row);
    const indexFields = cached?._indexFields?.[id];
    return indexFields === undefined ? [projected] : [{ ...projected, ...indexFields } as Row];
  });
}

function collectEntityData<Row>(
  results: readonly UseQueryResult<Row>[],
): readonly (Row | undefined)[] {
  return results.map((result) => result.data);
}

function hasAllEntityRows<Row>(rows: readonly (Row | undefined)[]): rows is readonly Row[] {
  return rows.every((row): row is Row => row !== undefined);
}
